'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isToday from 'dayjs/plugin/isToday';
import utc from 'dayjs/plugin/utc';
import isEqual from 'lodash.isequal';
import {
  EventClickArg,
  EventInput,
  EventSourceFunc,
  EventSourceFuncArg,
  MoreLinkArg,
  ViewMountArg,
} from '@fullcalendar/core';
import allLocales from '@fullcalendar/core/locales-all';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { $language } from '@nx-frontend/shared/store/app';
import { getBgClassNames, getBorderClassNames, getTextClassNames } from '@nx-frontend/shared/themes';
import {
  PortalBgType,
  PortalBorderType,
  PortalClassState,
  PortalTextType,
  TasksListType,
} from '@nx-frontend/shared/types';
import {
  renderDayAllDayContent,
  renderDayEventContent,
  renderDayHeaderContent,
  renderDayMoreLinkContent,
  renderEventContent,
  renderMonthDayCellContent,
  renderMonthHeaderContent,
  renderMonthMoreLinkContent,
  renderSlotLabelContent,
  renderWeekAllDayContent,
  renderWeekMoreLinkContent,
} from './calendar-components';
import { CalendarHeaderViewSwitch } from './calendar-view-switch';
import { MoreItemsPopover } from './more-tasks-popover';
import { $morePopover, setPopoverEvent, updatePopoverTasksEvent } from './store';
import './style.scss';
import { CalendarProps, CalendarViews, CustomEventClickArg, TasksFiltersState } from './types';
import {
  getDayTasks,
  getMonthWithMoreDaysInWeek,
  getMoreItemsPopoverPos,
  getMoreLinkOverlapEvents,
  getTimeGridMoreLinkDateTime,
  getUserTasksVars,
  mapFiltersToRequestQueryFilters,
  saveUserTasksVars,
} from './util';

dayjs.extend(isBetween);
dayjs.extend(isToday);
dayjs.extend(utc);

const PROCESS_SPEC_VIEWS = ['timeGridWeek', 'timeGridDay'];

const plusIcon = `
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
<path
  fill="#606060"
  fillRule="evenodd"
  d="M13 6a1 1 0 10-2 0v5H6a1 1 0 100 2h5v5a1 1 0 102 0v-5h5a1 1 0 100-2h-5V6z"
  clipRule="evenodd"
></path>
</svg>
`;

export function Calendar({
  featureId,
  workspaceId,
  tasks,
  completeTask: onCompleteTask,
  options,
  themeColors,
  themeStyle,
  controller,
  type,
  t,
}: CalendarProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const relativeContainerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<FullCalendar>(null);
  const todayButtonRef = useRef<HTMLButtonElement>(null);
  const headerDateRef = useRef<HTMLDivElement>(null);
  const [addViewClassName, setAddViewClassName] = useState('');
  const [tasksLodaing, setTasksLoading] = useState(false);
  const [setPopupState, morePopover, updatePopoverTasks, language] = useUnit([
    setPopoverEvent,
    $morePopover,
    updatePopoverTasksEvent,
    $language,
  ]);
  const touchX = useRef<number | null>(null);
  const taskLists = useRef<string[]>([]);

  const [activeTab, setActiveTab] = useState('todo');
  const [currentFilters, setCurrentFilters] = useState<TasksFiltersState | null>(null);

  const isMobile = useMemo(() => {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;
  }, []);

  const isTablet = useMemo(() => {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches;
  }, []);

  const completeTask = useCallback(
    (taskId: string, done: boolean) => {
      const inCalendarTask = calendarRef.current && calendarRef.current.getApi().getEventById(taskId);
      const workspaceId =
        inCalendarTask && 'workspaceId' in inCalendarTask.extendedProps && inCalendarTask.extendedProps.workspaceId
          ? (inCalendarTask.extendedProps.workspaceId as string)
          : undefined;
      onCompleteTask(taskId, done, workspaceId);
    },
    [onCompleteTask]
  );

  const processRenderMoreLink = () => {
    if (calendarRef.current && wrapperRef.current) {
      const currentView = calendarRef.current.getApi().view.type;
      if (PROCESS_SPEC_VIEWS.includes(currentView)) {
        const moreButtons = Array.from(wrapperRef.current.querySelectorAll('.fc-timegrid-more-link.fc-more-link'));
        moreButtons.forEach((btn) => {
          Array.from(
            (btn.closest('.fc-timegrid-col-events') as HTMLElement).querySelectorAll('.fc-timegrid-event-harness')
          ).forEach((taskEl) => {
            if (taskEl.classList.contains('hidden')) {
              taskEl.classList.remove('hidden');
            }
          });
        });
        for (const button of moreButtons) {
          const dateTime = getTimeGridMoreLinkDateTime(button as HTMLElement);
          if (dateTime) {
            button.setAttribute('data-date-time', dateTime);
            button.removeAttribute('title');
            const textNode = button.querySelector('.calendar-more-button') as HTMLElement;
            if (textNode) {
              const slotTasks = getMoreLinkOverlapEvents(button as HTMLElement);
              slotTasks.forEach((taskEl) => {
                taskEl.classList.add('hidden');
              });
              const currentHiddenTasksCount = parseInt(textNode.getAttribute('data-hidden-tasks') || '0');
              textNode.innerText = `${currentHiddenTasksCount + slotTasks.length}`;
            }
          }
        }
      }
    }
  };

  const processRemoveTitleAttrs = () => {
    if (calendarRef.current) {
      wrapperRef.current?.querySelectorAll('.fc-daygrid-more-link').forEach((node) => {
        node.removeAttribute('title');
      });
    }
  };

  const processSaveUserViewTypeIfNeed = useCallback(() => {
    if (calendarRef.current) {
      const currentView = calendarRef.current.getApi().view.type;
      const currentSavedView = getUserTasksVars(featureId)?.calendarView;
      if (currentView !== currentSavedView) {
        saveUserTasksVars(featureId, { calendarView: currentView });
      }
    }
  }, [featureId]);

  const processSetHeaderDateAndTodayState = useCallback(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      if (headerDateRef.current) {
        if (!isMobile) {
          headerDateRef.current.innerText = calendarApi.view.title;
        } else {
          if (calendarApi.view.type === 'timeGridWeek') {
            const dateTitle = getMonthWithMoreDaysInWeek(
              calendarApi.view.currentStart,
              calendarApi.view.currentEnd,
              'MMMM, YYYY'
            );
            if (dateTitle) {
              headerDateRef.current.innerText = dateTitle;
            } else {
              headerDateRef.current.innerText = dayjs(calendarApi.view.currentStart).format('MMMM, YYYY');
            }
          } else {
            headerDateRef.current.innerText = dayjs(calendarApi.view.currentStart).format('MMMM, YYYY');
          }
        }
      }
      if (todayButtonRef.current) {
        const currentView = calendarApi.view.type;
        const start = calendarApi.view.activeStart;
        const end = calendarApi.view.activeEnd;
        if (
          (dayjs().isBetween(start, end, 'day') &&
            (currentView === CalendarViews.MONTH || currentView === CalendarViews.WEEK)) ||
          (dayjs(calendarApi.view.currentStart).isToday() && currentView === 'timeGridDay')
        ) {
          if (todayButtonRef.current.classList.contains('is-active')) {
            todayButtonRef.current.classList.remove('is-active');
          }
        } else {
          if (!todayButtonRef.current.classList.contains('is-active')) {
            todayButtonRef.current.classList.add('is-active');
          }
        }
      }
    }
  }, [isMobile]);

  const postRenderProcess = useCallback(() => {
    processRenderMoreLink();
    processRemoveTitleAttrs();
    processSaveUserViewTypeIfNeed();
    processSetHeaderDateAndTodayState();
  }, [processSaveUserViewTypeIfNeed, processSetHeaderDateAndTodayState]);

  const onCreateTask = useCallback(
    ({ taskListId, task }: { taskListId: string; task: EventInput }) => {
      const apply = calendarRef.current && (type === TasksListType.DASHBOARD || taskLists.current.includes(taskListId));
      if (apply && task.id) {
        if (type === TasksListType.DASHBOARD) {
          if (activeTab === 'todo') {
            if (task.extendedProps && 'done' in task.extendedProps && task.extendedProps.done) {
              task.display = 'none';
            }
          }
        }
        calendarRef.current.getApi().addEvent(task);
      }
    },
    [type, activeTab]
  );

  const onUpdateTask = useCallback(
    ({ taskListId, task }: { taskListId: string; task: EventInput }) => {
      const apply = calendarRef.current && (type === TasksListType.DASHBOARD || taskLists.current.includes(taskListId));
      if (apply && task.id) {
        const originTask = calendarRef.current.getApi().getEventById(task.id);
        if (originTask) {
          originTask.remove();
        }
        if (type === TasksListType.DASHBOARD) {
          task.display =
            (activeTab === 'todo' && task.done) || (activeTab === 'completed' && !task.done) ? 'none' : undefined;
        }
        calendarRef.current.getApi().addEvent(task);
        if (morePopover !== null) {
          const tasksInPopover = Array.from(morePopover.tasks);
          const taskIndexInPopover = tasksInPopover.findIndex((item) => item.id === task.id);
          if (taskIndexInPopover >= 0) {
            if (task.done !== tasksInPopover[taskIndexInPopover].isDone) {
              if (type === TasksListType.DASHBOARD) {
                if ((activeTab === 'todo' && task.done) || (activeTab === 'completed' && !task.done)) {
                  tasksInPopover.splice(taskIndexInPopover, 1);
                }
              } else {
                tasksInPopover.splice(taskIndexInPopover, 1, {
                  ...tasksInPopover[taskIndexInPopover],
                  isDone: task.done,
                });
              }
              updatePopoverTasks(tasksInPopover);
            }
          }
        }
      }
    },
    [morePopover, updatePopoverTasks, type, activeTab]
  );

  const onRemoveTask = useCallback(
    ({ taskListId, taskId }: { taskListId: string; taskId: string }) => {
      const apply = calendarRef.current && (type === TasksListType.DASHBOARD || taskLists.current.includes(taskListId));
      if (apply) {
        const originTask = calendarRef.current.getApi().getEventById(taskId);
        if (originTask) {
          originTask.remove();
        }
      }
    },
    [type]
  );

  const onBoardUpdate = useCallback(
    ({ boardId, taskListIds }: { boardId: string; taskListIds?: string[] }) => {
      if (type === TasksListType.KANBAN && boardId === featureId && taskListIds) {
        taskLists.current = taskListIds;
      }
    },
    [featureId, type]
  );

  const updateTasksSource: EventSourceFunc = useCallback(
    async (info: EventSourceFuncArg): Promise<EventInput[]> => {
      try {
        setTasksLoading(true);
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.removeAllEvents();
        }
        // если в фильтрах указано показать только таски без даты, не делаем запрос для календаря
        if (currentFilters?.date.length === 1 && currentFilters.date[0] === 'noDates') {
          return [];
        }
        const { start, end } = info;
        const filters =
          currentFilters === null
            ? {
                workspaceIds: [workspaceId],
              }
            : mapFiltersToRequestQueryFilters(currentFilters);
        filters.deadlineIntervalStartMs = start.getTime();
        filters.deadlineIntervalEndMs = end.getTime();
        const loadedTasks = await controller.getTasks(filters);
        if (loadedTasks !== null) {
          const preparedTasks = loadedTasks.map((task) => {
            if ((activeTab === 'todo' && task.done) || (activeTab === 'completed' && !task.done)) {
              task.display = 'none';
            }
            return task;
          });
          return preparedTasks;
        }
      } finally {
        setTasksLoading(false);
      }
      return [];
    },
    [controller, workspaceId, currentFilters, activeTab]
  );

  const onDashboardTabChanged = useCallback(
    ({ featureId: dashboardId, tab }: { featureId: string; tab: string }) => {
      if (dashboardId === featureId && tab !== activeTab) {
        setActiveTab(tab);
      }
    },
    [featureId, activeTab]
  );

  const onFiltersChanged = useCallback(
    ({ featureId: dashboardId, filters }: { featureId: string; filters: TasksFiltersState }) => {
      if (dashboardId === featureId && !isEqual(filters, currentFilters)) {
        setCurrentFilters(filters);
      }
    },
    [featureId, currentFilters]
  );

  const onChangeCalendarView = useCallback(
    ({ featureId: id, tasks: initialTasks, force }: { featureId: string; tasks: EventInput[]; force: boolean }) => {
      if (calendarRef.current && featureId === id && (!taskLists.current.length || force)) {
        if (type === TasksListType.KANBAN) {
          taskLists.current = initialTasks.reduce((lists, task) => {
            if (
              task.extendedProps &&
              'taskListId' in task.extendedProps &&
              !lists.includes(task.extendedProps.taskListId)
            ) {
              lists.push(task.extendedProps.taskListId);
            }
            return lists;
          }, [] as string[]);
        } else {
          taskLists.current = [featureId];
        }
        if (force) {
          calendarRef.current.getApi().removeAllEventSources();
        }
        calendarRef.current.getApi().addEventSource(initialTasks);
        calendarRef.current.getApi().refetchEvents();
      }
    },
    [featureId, type]
  );

  useEffect(() => {
    if (type === TasksListType.DASHBOARD) {
      const savedFiltersJson = localStorage.getItem('dashboard-filter-' + workspaceId);
      if (savedFiltersJson) {
        try {
          const savedFilters = JSON.parse(savedFiltersJson);
          if (savedFilters && savedFilters.tab && savedFilters.tab !== 'todo') {
            setActiveTab(savedFilters.tab);
          }
          const { labels, assigneeIds, date, workspaceIds } = savedFilters;
          const initialFilters = {
            labels: labels || [],
            assigneeIds: assigneeIds || [],
            date: date || [],
            workspaceIds: workspaceIds || [workspaceId],
          };
          setCurrentFilters(initialFilters);
        } catch (_) {
          return;
        }
      } else {
        setCurrentFilters({
          labels: [],
          assigneeIds: [],
          date: [],
          workspaceIds: [workspaceId],
        });
      }
    }
  }, [workspaceId, type]);

  useEffect(() => {
    controller.emitter.on('createTask', onCreateTask);
    controller.emitter.on('updateTask', onUpdateTask);
    controller.emitter.on('removeTask', onRemoveTask);
    controller.emitter.on('dashboard-tab-changed', onDashboardTabChanged);
    controller.emitter.on('filters-changed', onFiltersChanged);
    controller.emitter.on('change-calendar-view', onChangeCalendarView);
    controller.emitter.on('boardUpdate', onBoardUpdate);
    return () => {
      controller.emitter.off('createTask', onCreateTask);
      controller.emitter.off('updateTask', onUpdateTask);
      controller.emitter.off('removeTask', onRemoveTask);
      controller.emitter.off('dashboard-tab-changed', onDashboardTabChanged);
      controller.emitter.off('filters-changed', onFiltersChanged);
      controller.emitter.off('change-calendar-view', onChangeCalendarView);
      controller.emitter.off('boardUpdate', onBoardUpdate);
    };
  }, [
    controller,
    onCreateTask,
    onUpdateTask,
    onRemoveTask,
    onDashboardTabChanged,
    onFiltersChanged,
    onChangeCalendarView,
    onBoardUpdate,
  ]);

  useEffect(() => {
    if (calendarRef.current) {
      const originalDidUpdate = calendarRef.current.componentDidUpdate.bind(calendarRef.current);
      calendarRef.current.componentDidUpdate = function () {
        originalDidUpdate();
        postRenderProcess();
      };
    }
  }, [postRenderProcess]);

  useEffect(() => {
    if (wrapperRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        calendarRef.current?.getApi().updateSize();
      });
      resizeObserver.observe(wrapperRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  const handleEventClick = useCallback(
    (clickInfo: EventClickArg | CustomEventClickArg) => {
      controller.openDetailTask(clickInfo.event.id, type);
    },
    [controller, type]
  );

  const handleAddNewTaskClick = useCallback(
    (date?: Date, showTime = false) => {
      const deadline = date ? dayjs(date).unix() : null;
      controller.createTask(taskLists.current[0], workspaceId, deadline, showTime);
    },
    [controller, workspaceId]
  );

  const onMonthDateClick = (event: DateClickArg) => {
    if (isMobile) {
      const date = event.date;
      const tasks = event.view.calendar.getEvents();
      const dayTasks = getDayTasks(date, tasks);
      if (dayTasks.length) {
        const mappedTasks = dayTasks.map((task) => ({
          id: task.id,
          title: task.title,
          start: task.start !== null ? task.start : undefined,
          isDone: 'done' in task.extendedProps && task.extendedProps.done ? true : false,
        }));
        setPopupState({
          featureId,
          date,
          tasks: mappedTasks,
          onTaskClick: handleEventClick,
          container: wrapperRef.current as HTMLDivElement,
          completeTask: completeTask,
        });
      }
    }
  };

  const onDateClick = (event: DateClickArg) => {
    if (!options.allowCreateNewTask) return;
    if (!event.allDay) {
      handleAddNewTaskClick(event.date, true);
    }
  };

  const onMoreLinkClick = useCallback(
    ({ date, allDay, allSegs, jsEvent: event, view }: MoreLinkArg) => {
      const target = event.target as HTMLElement;
      if (target) {
        const tasks = allSegs.map((task) => ({
          id: task.event.id,
          title: task.event.title,
          start: task.start,
          isDone: 'done' in task.event.extendedProps && task.event.extendedProps.done ? true : false,
        }));
        if (!allDay && PROCESS_SPEC_VIEWS.includes(view.type)) {
          const siblingTasks = getMoreLinkOverlapEvents(target.closest('.fc-timegrid-more-link') as HTMLElement).map(
            (el: Element) => {
              const taskDataNode = el.querySelector('span.calendar-event') as HTMLElement;
              return {
                id: taskDataNode.getAttribute('data-id') as string,
                title: taskDataNode.getAttribute('data-title') as string,
                start: new Date(parseInt(taskDataNode.getAttribute('data-date') as string)),
                isDone: taskDataNode.getAttribute('data-completed') === 'true' ? true : false,
              };
            }
          );
          if (siblingTasks.length) {
            tasks.unshift(...siblingTasks);
          }
        }
        const pos = getMoreItemsPopoverPos(
          (allDay ? target.closest('td') : target.closest('.fc-timegrid-more-link')) as HTMLElement,
          (type === TasksListType.DASHBOARD ? relativeContainerRef.current : wrapperRef.current) as HTMLElement
        );
        setPopupState({
          featureId,
          date,
          tasks,
          pos: !isMobile ? pos : undefined,
          onTaskClick: handleEventClick,
          container: wrapperRef.current as HTMLDivElement,
          completeTask: completeTask,
        });
      }
      return 'false'; // тут строка, так как этого требует тип возвращаемого значения функции
      //если вернуть void или undefined, календарь отрисует стандартный поповер
    },
    [handleEventClick, completeTask, setPopupState, featureId, isMobile, type]
  );

  const onHeaderPrevClick = useCallback(() => {
    calendarRef.current && calendarRef.current.getApi().prev();
  }, []);

  const onHeaderNextClick = useCallback(() => {
    calendarRef.current && calendarRef.current.getApi().next();
  }, []);

  const onHeaderTodayClick = () => {
    calendarRef.current && calendarRef.current.getApi().today();
  };

  const onViewChange = (view: CalendarViews) => {
    calendarRef.current && calendarRef.current.getApi().changeView(view);
  };

  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      if (morePopover !== null) return;
      if (wrapperRef.current) {
        if (wrapperRef.current.contains(event.touches[0].target as Node)) {
          touchX.current = event.touches[0].clientX;
        }
      }
    },
    [morePopover]
  );

  const handleTouchMove = useCallback(
    (event: TouchEvent) => {
      if (touchX.current === null) {
        return;
      }
      const diff = touchX.current - event.touches[0].clientX;

      if (Math.abs(diff) > 100) {
        if (diff > 0) {
          onHeaderNextClick();
        } else {
          onHeaderPrevClick();
        }
        touchX.current = null;
      }
    },
    [onHeaderNextClick, onHeaderPrevClick]
  );

  const handleTouchEnd = () => {
    if (touchX.current) {
      touchX.current = null;
    }
  };

  useEffect(() => {
    if (isMobile) {
      document.addEventListener('touchstart', handleTouchStart, false);
      document.addEventListener('touchmove', handleTouchMove, false);
      document.addEventListener('touchend', handleTouchEnd, false);

      return () => {
        document.removeEventListener('touchstart', handleTouchStart, false);
        document.removeEventListener('touchmove', handleTouchMove, false);
        document.removeEventListener('touchend', handleTouchEnd, false);
      };
    }
  }, [isMobile, handleTouchMove, handleTouchStart]);

  const onViewDidMount = useCallback((viewInfo: ViewMountArg) => {
    if (viewInfo.view.type === CalendarViews.MONTH) {
      setAddViewClassName('fc-month-view-mounted');
    } else {
      setAddViewClassName('fc-week-day-view-mounted');
    }
  }, []);

  const onTimeGridViewDidMount = useCallback(
    function (props: { el: HTMLElement }) {
      if (!options.allowCreateNewTask) return;
      const { el } = props;
      const lanes = el.querySelectorAll('td.fc-timegrid-slot.fc-timegrid-slot-lane');
      const onMouseEnter = function (event: Event) {
        const viewClassName = el.getAttribute('class') || '';
        const headerCell = el.querySelector('th.fc-col-header-cell') as HTMLElement;
        if (!headerCell) {
          return;
        }

        const lane = event.target as HTMLElement;
        if (!lane) {
          return;
        }
        if (lane.innerHTML && viewClassName === lane.getAttribute('built-for-view-class')) {
          return;
        } else {
          // remove all children of lane
          while (lane.firstChild) {
            lane.removeChild(lane.firstChild);
          }
          lane.setAttribute('built-for-view-class', viewClassName);
          const cellWidth = headerCell.offsetWidth;
          const countColumns = el.querySelectorAll('th.fc-col-header-cell').length;
          lane.style.height = window.getComputedStyle(lane).height;
          const container = document.createElement('div');
          container.className = 'calendar-hover-cell-container';
          lane.appendChild(container);
          const cellHeight = lane.offsetHeight;
          for (let i = 0; i < countColumns; i++) {
            const div = document.createElement('div');
            div.className = 'calendar-hover-cell-with-plus';
            div.style.width = cellWidth + 3 + 'px';
            div.style.height = cellHeight - 1 + 'px';
            div.innerHTML = `
            <div class="calendar-hover-cell-with-plus-icon">
              ${plusIcon}
            </div>
          `;
            container.appendChild(div);
          }
        }
      };
      lanes.forEach((lane) => {
        lane.addEventListener('mouseenter', onMouseEnter, false);
      });
    },
    [options.allowCreateNewTask]
  );

  return (
    <div
      className={classNames(`flex-auto relative fuse-base-tasks ${addViewClassName}`, {
        'calendar-compact': isMobile,
        'calendar-tablet': isTablet && !isMobile,
        'h-full flex flex-col': type === TasksListType.DASHBOARD,
      })}
      ref={wrapperRef}
    >
      <div className="fc-header-toolbar flex items-center justify-between mb-6">
        <div className="fc-header-prev-next flex items-center">
          <button
            className={classNames(
              'fc-prev-button border p-1',
              themeColors && themeStyle
                ? [
                    ...getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE_INNER),
                    ...getBgClassNames(themeColors, PortalBgType.SURFACE),
                    ...getBgClassNames(themeColors, PortalBgType.SURFACE_INNER_HOVER, [PortalClassState.HOVER]),
                  ]
                : 'border-neutral-200 hover:bg-neutral-200'
            )}
            onClick={onHeaderPrevClick}
          >
            <Icon
              icon={IconsMap.ARROW_2_LEFT}
              className={classNames(
                'w-4',
                themeColors ? getTextClassNames(themeColors, PortalTextType.SURFACE_3) : 'text-neutral-500'
              )}
            />
          </button>
          <button
            className={classNames(
              'fc-next-button border p-1 border-l-0',
              themeColors && themeStyle
                ? [
                    ...getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE_INNER),
                    ...getBgClassNames(themeColors, PortalBgType.SURFACE),
                    ...getBgClassNames(themeColors, PortalBgType.SURFACE_INNER_HOVER, [PortalClassState.HOVER]),
                  ]
                : 'border-neutral-200 hover:bg-neutral-200'
            )}
            onClick={onHeaderNextClick}
          >
            <Icon
              icon={IconsMap.ARROW_2_RIGHT}
              className={classNames(
                'w-4',
                themeColors ? getTextClassNames(themeColors, PortalTextType.SURFACE_3) : 'text-neutral-500'
              )}
            />
          </button>
        </div>
        <button
          className={classNames(
            'fc-header-today rounded border text-xs px-3 py-1 ml-2',
            themeColors && themeStyle
              ? [
                  ...getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE_INNER),
                  ...getBgClassNames(themeColors, PortalBgType.SURFACE),
                  ...getBgClassNames(themeColors, PortalBgType.SURFACE_INNER_HOVER, [PortalClassState.HOVER]),
                ]
              : 'border-neutral-200 hover:bg-neutral-200'
          )}
          onClick={onHeaderTodayClick}
          ref={todayButtonRef}
        >
          {t('tasks.today')}
        </button>
        <div
          className={classNames(
            'fc-header-current-date shrink grow text-center text-lg font-semibold whitespace-nowrap',
            themeColors ? getTextClassNames(themeColors, PortalTextType.SURFACE) : 'text-nuetral-800'
          )}
          ref={headerDateRef}
        ></div>
        <CalendarHeaderViewSwitch
          featureId={featureId}
          initialView={(options.initialView as CalendarViews) || CalendarViews.MONTH}
          onChange={onViewChange}
          themeColors={themeColors}
          themeStyle={themeStyle}
          t={t}
        />
      </div>
      <div
        className={classNames({
          'relative h-full overflow-y-auto mr-[-2.5rem] pr-10': type === TasksListType.DASHBOARD,
          'contents': type !== TasksListType.DASHBOARD,
        })}
        ref={relativeContainerRef}
      >
        {type === TasksListType.DASHBOARD && tasksLodaing && (
          <div className="absolute w-full h-full top-0 left-0 flex items-center justify-center bg-neutral-500 bg-opacity-10 z-20 rounded-md">
            <Spinner />
          </div>
        )}
        <FullCalendar
          slotLaneClassNames={!options.allowCreateNewTask ? 'disabled-create-task' : ''}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          headerToolbar={false}
          initialView={options.initialView || CalendarViews.MONTH}
          dayMaxEventRows={5}
          weekends={true}
          nowIndicator={true}
          initialEvents={tasks}
          eventContent={renderEventContent.bind(null, { completeTask })}
          eventClick={handleEventClick}
          height={options.height || 'auto'}
          slotLabelContent={renderSlotLabelContent.bind(null, { isMobile })}
          viewDidMount={onViewDidMount}
          moreLinkClick={onMoreLinkClick}
          eventDisplay="block"
          locales={allLocales}
          locale={language}
          events={type === TasksListType.DASHBOARD ? updateTasksSource : undefined}
          views={{
            dayGridMonth: {
              dateClick: onMonthDateClick,
              dayHeaderContent: renderMonthHeaderContent.bind(null, { isMobile }),
              moreLinkContent: renderMonthMoreLinkContent,
              dayCellContent: renderMonthDayCellContent.bind(null, {
                isMobile,
                allowCreateNewTask: options.allowCreateNewTask,
                onCreateTask: handleAddNewTaskClick,
                morePopover,
                themeColors,
                themeStyle,
              }),
            },
            timeGridWeek: {
              titleFormat: {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              },
              dayHeaderFormat: { day: 'numeric', weekday: 'short' },
              dayMaxEventRows: !isMobile ? 5 : 1,
              slotEventOverlap: false,
              eventOverlap: false,
              eventMinHeight: !isMobile ? 22 : 54,
              eventMaxStack: !isMobile && !isTablet ? 3 : 1,
              dateClick: onDateClick,
              moreLinkContent: renderWeekMoreLinkContent.bind(null, { isMobile }),
              dayHeaderContent: renderDayHeaderContent.bind(null, {
                isMobile,
                allowCreateNewTask: options.allowCreateNewTask,
                onCreateTask: handleAddNewTaskClick,
              }),
              viewDidMount: onTimeGridViewDidMount,
              allDayContent: renderWeekAllDayContent.bind(null, { isMobile }),
            },
            timeGridDay: {
              dayMaxEventRows: 3,
              slotEventOverlap: false,
              eventOverlap: false,
              eventMinHeight: !isMobile ? 26 : 54,
              eventMaxStack: isMobile ? 3 : undefined,
              dayHeaderContent: renderDayHeaderContent.bind(null, {
                isMobile,
                allowCreateNewTask: options.allowCreateNewTask,
                onCreateTask: handleAddNewTaskClick,
              }),
              dateClick: onDateClick,
              moreLinkContent: renderDayMoreLinkContent,
              viewDidMount: onTimeGridViewDidMount,
              eventContent: renderDayEventContent.bind(null, { isMobile, completeTask }),
              allDayContent: renderDayAllDayContent.bind(null, { isMobile }),
            },
          }}
          ref={calendarRef}
        />
        <MoreItemsPopover key={`${featureId}-calendar-popover`} featureId={featureId} />
      </div>
    </div>
  );
}
